import {connect} from 'react-redux';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {RuleBundleTypesEnum} from '../../../../../models/api/ruleCommonTypes';
import {AppState} from '../../../../../store/appState';
import {getHasPermission} from '../../../../../store/data/access/accessSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {ActivityRulePill} from './activityRulePill';

/*
 * Props.
 */

interface ActivityRulePillContainerProps {
  ruleId: number;
  ruleBundleId?: number;
  ruleBundleType?: RuleBundleTypesEnum;
  ruleNamespace?: string;
  onClickTrackingCallback?: Handler;
}

/*
 * Selectors.
 */

function getRuleNamespace(_state: AppState, props: ActivityRulePillContainerProps) {
  return props.ruleNamespace;
}

function makeGetIsRuleAccessible() {
  return createSelector([getHasPermission, getRuleNamespace], (hasPermission, ruleNamespace) =>
    ruleNamespace ? hasPermission(ruleNamespace, PermissionsEnum.RULE_UPDATE) : false,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ActivityRulePillContainerProps>()(() => ({
  ruleNamespace: getRuleNamespace,
  isRuleAccessible: makeGetIsRuleAccessible(),
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
}));

export const ActivityRulePillContainer = connect(
  mapStateToProps,
  mapDispatchToProps,

  (stateProps, dispatchProps, ownProps: ActivityRulePillContainerProps) => {
    const onActivityRuleClick = () => {
      if (ownProps.onClickTrackingCallback) {
        ownProps.onClickTrackingCallback();
      }

      const {ruleNamespace} = stateProps;
      if (!ruleNamespace) {
        return;
      }

      // Determine if we should use the settings route for a bundle, or if we should use the old rule route.
      const settingsPath = computeBundlePath(ownProps, ruleNamespace);
      if (!settingsPath) {
        return;
      }

      dispatchProps.navigateToLink(settingsPath);
    };

    return {
      ...ownProps,
      ...stateProps,
      ...dispatchProps,
      onActivityRuleClick,
    };
  },
)(ActivityRulePill);

/*
 * Helpers.
 */

const supportedBundleTypes: RuleBundleTypesEnum[] = [
  RuleBundleTypesEnum.SINGLE,
  RuleBundleTypesEnum.SLA,
  RuleBundleTypesEnum.CSAT,
];
function computeBundlePath(ownProps: ActivityRulePillContainerProps, ruleNamespace: string) {
  const {ruleBundleId, ruleId, ruleBundleType} = ownProps;
  if (!ruleBundleId || !ruleBundleType) {
    return null;
  }

  if (!supportedBundleTypes.includes(ruleBundleType)) {
    return null;
  }

  switch (ruleBundleType) {
    case RuleBundleTypesEnum.CSAT:
      return `/settings/${ruleNamespace}/rules/recipes/csat/${ruleBundleId}`;
    case RuleBundleTypesEnum.SLA:
      return `/settings/${ruleNamespace}/rules/recipes/${ruleBundleId}`;
    default:
      // default to single case
      return `/settings/${ruleNamespace}/rules/edit/${ruleId}`;
  }
}
